import type { FC } from "react";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import type { ChipsFieldDefinition, Schema } from "../Form/types";

export interface QuickFilterProps {
  quickFilter?: Schema;
  currentFilter?: Record<string, unknown>;
  onFilterChange?: (filter: Record<string, unknown>) => void;
}

/**
 * Фильтры, вынесенные из формы в чипы: поля схемы с priority: "quick".
 * Повторный клик по выбранному чипу снимает фильтр.
 */
export const QuickFilter: FC<QuickFilterProps> = ({
  quickFilter,
  currentFilter = {},
  onFilterChange,
}) => {
  if (!quickFilter || Object.keys(quickFilter).length === 0) return null;

  const handleQuickFilterClick = (fieldKey: string, value: string | number) => {
    const isSame = currentFilter[fieldKey] === value;

    const filter = isSame
      ? Object.fromEntries(
          Object.entries(currentFilter).filter(([key]) => key !== fieldKey)
        )
      : { ...currentFilter, [fieldKey]: value };

    onFilterChange?.(filter);
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      flexWrap="wrap"
      rowGap={1}
      sx={{ width: "100%" }}
    >
      {Object.entries(quickFilter).map(([fieldKey, field]) => {
        if (!field || !("options" in field)) return null;

        const options = (field.options ??
          []) as ChipsFieldDefinition["options"];

        return options.map((option) => {
          const isSelected =
            currentFilter[fieldKey] === option.value ||
            (option.value === "" && !currentFilter[fieldKey]);

          const chip = (
            <Chip
              key={`${fieldKey}-${option.value}`}
              label={option.label}
              color={isSelected ? "primary" : "default"}
              variant="filled"
              onClick={() => handleQuickFilterClick(fieldKey, option.value)}
              size="small"
            />
          );

          return option.tooltip ? (
            <Tooltip
              key={`${fieldKey}-${option.value}`}
              title={option.tooltip}
              arrow
            >
              {chip}
            </Tooltip>
          ) : (
            chip
          );
        });
      })}
    </Stack>
  );
};
